/**
 * MilestoneForm Component
 * Form for creating/editing milestones with Zod validation
 * Feature flag: ENABLE_SERVICE_ITEM_ASSIGNMENT for assignee field
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { createItemMilestoneSchema, type CreateItemMilestone, type ItemMilestone } from '@/schemas/milestone';
import { useCreateMilestone, useUpdateMilestone } from '@/hooks/milestones/useItemMilestones';
import { useMilestoneTypes } from '@/hooks/milestones/useMilestoneTypes';
import { useProjectTeamMembers } from '@/hooks/useProjectTeamMembers';
// ✅ HARDCODED (PR-F1 2026-04-30): ENABLE_SERVICE_ITEM_ASSIGNMENT retired
import { logger } from '@/lib/logger';
import { User } from 'lucide-react';

interface MilestoneFormProps {
  itemId: string;
  projectId: string; // ✅ Required for fetching team members
  projectStatus?: string; // ✅ Pass project status for service item assignment
  itemStartDate: string;
  itemEndDate: string;
  milestone?: ItemMilestone | null;
  onSuccess: () => void;
  onCancel: () => void;
  /** Loại mốc chọn sẵn khi TẠO MỚI (vd nhóm mốc → event_input). Không truyền = loại đầu tiên như cũ. */
  defaultMilestoneType?: string;
}

export const MilestoneForm = ({ 
  itemId, 
  projectId,
  projectStatus,
  itemStartDate, 
  itemEndDate, 
  milestone, 
  onSuccess, 
  onCancel,
  defaultMilestoneType 
}: MilestoneFormProps) => {
  const createMutation = useCreateMilestone();
  const updateMutation = useUpdateMilestone();
  const { data: milestoneTypes = [] } = useMilestoneTypes();

  // Chỉ dùng loại mặc định nếu nó có trong danh sách — không thì Select hiện trống
  const initialType =
    (defaultMilestoneType && milestoneTypes.some(t => t.name === defaultMilestoneType))
      ? defaultMilestoneType
      : milestoneTypes[0]?.name;

  // ✅ HARDCODED (PR-F1 2026-04-30): ENABLE_SERVICE_ITEM_ASSIGNMENT retired, isServiceProject guard preserved
  const isServiceProject = projectStatus === 'service';
  const showAssigneeField = isServiceProject;

  // Fetch team members for assignee dropdown (only when needed)
  const { data: teamMembers = [] } = useProjectTeamMembers(
    projectId,
    { enabled: showAssigneeField && !!projectId }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateItemMilestone>({
    resolver: zodResolver(createItemMilestoneSchema),
    defaultValues: milestone ? {
      item_id: milestone.item_id,
      name: milestone.name,
      milestone_type: milestone.milestone_type,
      start_date: milestone.start_date,
      end_date: milestone.end_date,
      status: milestone.status,
      completion_percentage: milestone.completion_percentage,
      notes: milestone.notes || '',
      sort_order: milestone.sort_order,
      assigned_user_id: milestone.assigned_user_id || null,
    } : {
      item_id: itemId,
      name: '',
      milestone_type: initialType || '',
      start_date: itemStartDate,
      end_date: itemEndDate,
      status: 'in_progress',
      completion_percentage: 0,
      notes: '',
      sort_order: 0,
      assigned_user_id: null,
    },
  });

  useEffect(() => {
    if (!milestone && initialType) {
      setValue('milestone_type', initialType);
    }
  }, [initialType, milestone, setValue]);

  const onSubmit = async (data: CreateItemMilestone) => {
    try {
      if (milestone) {
        await updateMutation.mutateAsync({
          id: milestone.id,
          updates: {
            name: data.name,
            milestone_type: data.milestone_type,
            start_date: data.start_date,
            end_date: data.end_date,
            status: data.status,
            completion_percentage: data.completion_percentage,
            notes: data.notes,
            sort_order: data.sort_order,
            assigned_user_id: data.assigned_user_id,
          },
        });
        logger.info('MilestoneForm', 'Milestone updated', { milestoneId: milestone.id });
      } else {
        await createMutation.mutateAsync(data);
        logger.info('MilestoneForm', 'Milestone created', { itemId });
      }
      reset();
      onSuccess();
    } catch (error) {
      logger.error('MilestoneForm', 'Failed to save milestone', { error });
    }
  };

  const selectedType = watch('milestone_type');
  const selectedStatus = watch('status');
  const selectedAssignee = watch('assigned_user_id');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <Label htmlFor="name">Milestone Name *</Label>
        <Input
          id="name"
          {...register('name')}
          placeholder="e.g., Foundation Complete"
          className={errors.name ? 'border-destructive' : ''}
        />
        {errors.name && (
          <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="milestone_type">Type *</Label>
        <Select value={selectedType} onValueChange={(value) => setValue('milestone_type', value)}>
          <SelectTrigger className={errors.milestone_type ? 'border-destructive' : ''}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {milestoneTypes.map((type) => (
              <SelectItem key={type.name} value={type.name}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: type.color }}
                  />
                  {type.display_name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.milestone_type && (
          <p className="text-sm text-destructive mt-1">{errors.milestone_type.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="start_date">Start Date *</Label>
          <Input
            id="start_date"
            type="date"
            {...register('start_date')}
            min={itemStartDate}
            max={itemEndDate}
            className={errors.start_date ? 'border-destructive' : ''}
          />
          {errors.start_date && (
            <p className="text-sm text-destructive mt-1">{errors.start_date.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="end_date">End Date *</Label>
          <Input
            id="end_date"
            type="date"
            {...register('end_date')}
            min={itemStartDate}
            max={itemEndDate}
            className={errors.end_date ? 'border-destructive' : ''}
          />
          {errors.end_date && (
            <p className="text-sm text-destructive mt-1">{errors.end_date.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="status">Status *</Label>
          <Select value={selectedStatus} onValueChange={(value: any) => setValue('status', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="completion_percentage">Completion %</Label>
          <Input
            id="completion_percentage"
            type="number"
            min="0"
            max="100"
            {...register('completion_percentage', { valueAsNumber: true })}
            className={errors.completion_percentage ? 'border-destructive' : ''}
          />
          {errors.completion_percentage && (
            <p className="text-sm text-destructive mt-1">{errors.completion_percentage.message}</p>
          )}
        </div>
      </div>

      {/* ✅ Assignee Field - Only show for service projects with flag enabled */}
      {showAssigneeField && (
        <div>
          <Label htmlFor="assigned_user_id">Assigned To</Label>
          <Select 
            value={selectedAssignee || 'unassigned'} 
            onValueChange={(value) => setValue('assigned_user_id', value === 'unassigned' ? null : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Chọn người phụ trách...">
                {selectedAssignee ? (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {teamMembers.find(m => m.id === selectedAssignee)?.full_name || 'Unknown'}
                  </div>
                ) : (
                  <span className="text-muted-foreground">Chưa phân công</span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">
                <span className="text-muted-foreground">Chưa phân công</span>
              </SelectItem>
              {teamMembers.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={member.avatar_url || undefined} />
                      <AvatarFallback className="text-xs">
                        {member.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    {member.full_name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label htmlFor="notes">Notes (Optional)</Label>
        <Textarea
          id="notes"
          {...register('notes')}
          placeholder="Additional details..."
          rows={3}
          className={errors.notes ? 'border-destructive' : ''}
        />
        {errors.notes && (
          <p className="text-sm text-destructive mt-1">{errors.notes.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={createMutation.isPending || updateMutation.isPending}
        >
          {createMutation.isPending || updateMutation.isPending 
            ? 'Saving...' 
            : milestone ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
};
